/**
 * Test ID utilities for MultiSelect component
 *
 * Pattern: All test IDs are generated via named setter functions.
 * This ensures consistency between components and tests.
 */

import { generateTestId } from "@client-shared";

/**
 * Generate test ID for MultiSelect root wrapper
 * @param identifier - MultiSelect instance identifier
 * @returns Test ID string
 */
export const setMultiSelectTestId = (identifier: string): string =>
  generateTestId("MultiSelect", identifier.replace(/\s+/g, "-").toLowerCase());

/**
 * Generate test ID for MultiSelect trigger button
 * @param identifier - MultiSelect instance identifier
 * @returns Test ID string
 */
export const setMultiSelectTriggerTestId = (identifier: string): string =>
  generateTestId("MultiSelectTrigger", identifier.replace(/\s+/g, "-").toLowerCase());

/**
 * Generate test ID for MultiSelect popover content
 * @param identifier - MultiSelect instance identifier
 * @returns Test ID string
 */
export const setMultiSelectContentTestId = (identifier: string): string =>
  generateTestId("MultiSelectContent", identifier.replace(/\s+/g, "-").toLowerCase());

/**
 * Generate test ID for MultiSelect search input
 * @param identifier - MultiSelect instance identifier
 * @returns Test ID string
 */
export const setMultiSelectSearchTestId = (identifier: string): string =>
  generateTestId("MultiSelectSearch", identifier.replace(/\s+/g, "-").toLowerCase());

/**
 * Generate test ID for a MultiSelect option button.
 * Includes the instance identifier to avoid collisions when multiple
 * MultiSelect components with overlapping option values are on the same page.
 * @param identifier - MultiSelect instance identifier
 * @param value - Option value
 * @returns Test ID string
 */
export const setMultiSelectOptionTestId = (identifier: string, value: string): string =>
  generateTestId("MultiSelectOption", `${identifier}-${value}`.replace(/\s+/g, "-").toLowerCase());

/**
 * Generate test ID for a selected value pill button.
 * Includes the instance identifier to avoid collisions when multiple
 * MultiSelect components with overlapping option values are on the same page.
 * @param identifier - MultiSelect instance identifier
 * @param value - Selected option value
 * @returns Test ID string
 */
export const setMultiSelectPillTestId = (identifier: string, value: string): string =>
  generateTestId("MultiSelectPill", `${identifier}-${value}`.replace(/\s+/g, "-").toLowerCase());

/**
 * Generate test ID for the clear-all button
 * @param identifier - MultiSelect instance identifier
 * @returns Test ID string
 */
export const setMultiSelectClearTestId = (identifier: string): string =>
  generateTestId("MultiSelectClear", identifier.replace(/\s+/g, "-").toLowerCase());
